import type { DiagnosticRow } from '@nowline/browser';
import { useCallback, useEffect, useRef, useState } from 'react';
import { generativeAiSource } from './examples/index.ts';
import './App.css';
import type { LoadedFrom } from '@nowline-app/share';
import { parseLocationHash, useInitialSource } from '@nowline-app/share';
import type { ViewMode } from './chrome/AppHeader.tsx';
import { AppHeader } from './chrome/AppHeader.tsx';
import { Splash } from './chrome/Splash.tsx';
import { EditorPane } from './EditorPane.tsx';
import { Layout } from './Layout.tsx';
import { PreviewPane } from './PreviewPane.tsx';
import { Notice } from './share/Notice.tsx';
import { useTheme } from './theme/useTheme.ts';

const VIEW_MODE_KEY = 'nl-view-mode';

function getInitialViewMode(): ViewMode {
    // Default to 'diagram' when opened from a share link (obvious "Edit" affordance via header).
    const hash = parseLocationHash(location.hash);
    if (hash.kind !== 'none') return 'diagram';
    try {
        const stored = localStorage.getItem(VIEW_MODE_KEY);
        if (stored === 'editor' || stored === 'both' || stored === 'diagram') return stored;
    } catch {
        // storage unavailable
    }
    return 'both';
}

export function App() {
    const initial = useInitialSource();
    const { theme, resolvedTheme, setTheme } = useTheme();

    const [source, setSource] = useState(generativeAiSource);
    const [hasReset, setHasReset] = useState(false);
    const [lspDiagnostics, setLspDiagnostics] = useState<DiagnosticRow[]>([]);
    // Narrow: viewport ≤879px — the breakpoint at which layout--both stacks.
    // Initialised eagerly from matchMedia so the first render is already correct.
    const [isNarrow, setIsNarrow] = useState(() => window.matchMedia('(max-width: 879px)').matches);
    const [viewMode, setViewMode] = useState<ViewMode>(() => {
        const m = getInitialViewMode();
        // Coerce 'both' to 'editor' when the page loads narrow so the layout
        // never renders the stacked split on first paint.
        return m === 'both' && window.matchMedia('(max-width: 879px)').matches ? 'editor' : m;
    });
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [editorReady, setEditorReady] = useState(false);
    const appRootRef = useRef<HTMLDivElement>(null);
    // Stable ref so the matchMedia listener can read the latest viewMode
    // without being re-registered on every state change.
    const viewModeRef = useRef(viewMode);
    useEffect(() => {
        viewModeRef.current = viewMode;
    });

    // Track whether the source has been modified from the loaded/example value.
    const initialSourceRef = useRef(generativeAiSource);

    useEffect(() => {
        if (hasReset || initial.source === null) return;
        setSource(initial.source);
        initialSourceRef.current = initial.source;
    }, [initial.source, hasReset]);

    // Persist viewMode choice.
    useEffect(() => {
        try {
            localStorage.setItem(VIEW_MODE_KEY, viewMode);
        } catch {
            // storage unavailable
        }
    }, [viewMode]);

    // Fullscreen: listen for browser exits (Esc key, etc.).
    useEffect(() => {
        function handleFullscreenChange() {
            setIsFullscreen(Boolean(document.fullscreenElement));
        }
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    // Narrow breakpoint: unify with the layout--both flex-direction switch at 880px.
    // When the viewport narrows below 879px and viewMode is 'both', coerce to 'editor'
    // so the layout never stacks awkwardly. Widening keeps whatever mode is active.
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 879px)');
        function handleChange(e: MediaQueryListEvent) {
            const narrow = e.matches;
            setIsNarrow(narrow);
            if (narrow && viewModeRef.current === 'both') {
                setViewMode('editor');
            }
        }
        mq.addEventListener('change', handleChange);
        return () => mq.removeEventListener('change', handleChange);
    }, []);

    const loadedFrom: LoadedFrom = hasReset ? { kind: 'none' } : initial.loadedFrom;
    const loadError = hasReset ? null : initial.loadError;

    const lspErrors = lspDiagnostics.filter((d) => d.severity === 'error');
    const showNotice = loadError !== null || lspErrors.length > 0;
    const showLoading = !hasReset && initial.isLoading;

    const isDirty = source !== initialSourceRef.current;

    function resetToExample() {
        setSource(generativeAiSource);
        initialSourceRef.current = generativeAiSource;
        setHasReset(true);
        history.replaceState(null, '', location.pathname);
    }

    function handleLoadExample(exampleSource: string) {
        setSource(exampleSource);
        initialSourceRef.current = exampleSource;
        setHasReset(true);
        history.replaceState(null, '', location.pathname);
    }

    const handleEditorReady = useCallback(() => {
        setEditorReady(true);
    }, []);

    function toggleFullscreen() {
        const el = appRootRef.current;
        if (!el) return;
        if (document.fullscreenElement) {
            void document.exitFullscreen();
        } else {
            void el.requestFullscreen();
        }
    }

    return (
        <div ref={appRootRef} className='app-shell'>
            <Splash visible={!editorReady} />

            <AppHeader
                source={source}
                loadedFrom={loadedFrom}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                theme={theme}
                onThemeChange={setTheme}
                isFullscreen={isFullscreen}
                onFullscreenToggle={toggleFullscreen}
                onLoadExample={handleLoadExample}
                isDirty={isDirty}
                bothDisabled={isNarrow}
            />

            {showLoading && (
                <div className='notice notice--loading' aria-live='polite'>
                    Loading shared diagram…
                </div>
            )}
            {showNotice && !showLoading && <Notice error={loadError} onReset={resetToExample} />}

            {/* Workspace background (dot-grid) + floating card */}
            <div className='workspace-bg'>
                <div className='workspace-card'>
                    <Layout
                        viewMode={viewMode}
                        editor={
                            <EditorPane
                                value={source}
                                onChange={setSource}
                                onDiagnostics={setLspDiagnostics}
                                onReady={handleEditorReady}
                                theme={resolvedTheme}
                            />
                        }
                        preview={
                            <PreviewPane
                                source={source}
                                lspDiagnostics={lspDiagnostics}
                                resolvedTheme={resolvedTheme}
                            />
                        }
                    />
                </div>
            </div>
        </div>
    );
}
